'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from './utils';

const DIALOG_LAYER_Z_INDEX = 12000;
const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const TITLE_ID = 'teame-299-dialog-title';
const DESCRIPTION_ID = 'teame-299-dialog-description';

type ClickableChildProps = React.HTMLAttributes<Element> & {
  onClick?: (event: React.MouseEvent<Element>) => void;
};

type DialogContextValue = {
  open: boolean;
  setOpen: (nextOpen: boolean) => void;
};

const DialogContext = React.createContext<DialogContextValue>({
  open: false,
  setOpen: () => {},
});

function Dialog({
  open,
  onOpenChange,
  children,
}: {
  open?: boolean;
  onOpenChange?: (nextOpen: boolean) => void;
  children: React.ReactNode;
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = open !== undefined;
  const resolvedOpen = isControlled ? open : internalOpen;

  const setOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  return <DialogContext.Provider value={{ open: resolvedOpen, setOpen }}>{children}</DialogContext.Provider>;
}

function DialogTrigger({
  children,
  asChild,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean;
  children: React.ReactNode;
}) {
  const { setOpen } = React.useContext(DialogContext);
  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<ClickableChildProps>;
    return React.cloneElement(child, {
      ...props,
      onClick: (event: React.MouseEvent) => {
        child.props.onClick?.(event);
        if (!event.defaultPrevented) {
          setOpen(true);
        }
      },
    });
  }

  return (
    <button type="button" onClick={() => setOpen(true)} {...props}>
      {children}
    </button>
  );
}

function DialogPortal({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  return createPortal(children, document.body);
}

function DialogClose({
  children,
  asChild,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean;
  children: React.ReactNode;
}) {
  const { setOpen } = React.useContext(DialogContext);
  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<ClickableChildProps>;
    return React.cloneElement(child, {
      ...props,
      onClick: (event: React.MouseEvent) => {
        child.props.onClick?.(event);
        if (!event.defaultPrevented) {
          setOpen(false);
        }
      },
    });
  }

  return (
    <button type="button" onClick={() => setOpen(false)} {...props}>
      {children}
    </button>
  );
}

function DialogOverlay({ className, onClick, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const { open, setOpen } = React.useContext(DialogContext);
  if (!open) {
    return null;
  }

  return (
    <div
      data-slot="dialog-overlay"
      className={cn('tw-fixed tw-inset-0 tw-backdrop-blur-[1px]', className)}
      style={{
        // TODO(TEAME-183): Replace explicit overlay fallback with shared app-level modal tokens.
        backgroundColor: 'rgba(0, 0, 0, 0.55)',
        zIndex: DIALOG_LAYER_Z_INDEX,
      }}
      onClick={event => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          setOpen(false);
        }
      }}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  style,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
}) {
  const { open, setOpen } = React.useContext(DialogContext);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const body = document.body;
    const originalOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    const focusTarget = contentRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? contentRef.current;
    focusTarget?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [open, setOpen]);

  if (!open) {
    return null;
  }

  return (
    <DialogPortal>
      <div
        data-slot="dialog-layer"
        className="tw-fixed tw-inset-0 tw-pointer-events-none"
        style={{ zIndex: DIALOG_LAYER_Z_INDEX }}
      >
        <DialogOverlay />
      </div>
      <div
        ref={contentRef}
        data-slot="dialog-content"
        className={cn(
          'tw-fixed tw-left-[50%] tw-top-[50%] tw-grid tw-w-full tw-max-w-[calc(100%-2rem)] tw-translate-x-[-50%] tw-translate-y-[-50%] tw-gap-4 tw-rounded-lg tw-border tw-p-6 sm:tw-max-w-lg tw-pointer-events-auto',
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-describedby={DESCRIPTION_ID}
        tabIndex={-1}
        style={{
          // TODO(TEAME-183): Replace CSS-var fallbacks with shared semantic Tailwind utilities
          // once project-level token mapping supports prefixed card/accent/ring classes.
          backgroundColor: 'var(--card)',
          color: 'var(--card-foreground)',
          borderColor: 'var(--border)',
          boxShadow: 'var(--elevation-lg, 0 25px 50px -12px rgba(0,0,0,0.25))',
          zIndex: DIALOG_LAYER_Z_INDEX + 1,
          ...style,
        }}
        {...props}
      >
        {children}
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="focus:tw-ring-blue-500 tw-absolute tw-right-3 tw-top-3 tw-h-10 tw-w-10 tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-transparent tw-text-neutral-600 tw-opacity-80 tw-transition-all hover:tw-bg-neutral-100 hover:tw-text-neutral-950 hover:tw-opacity-100 focus:tw-ring-2 focus:tw-outline-hidden disabled:tw-pointer-events-none"
          aria-label="Close dialog"
          // TODO(TEAME-183): Use shared close-button variant once global modal primitives are adopted.
          style={{ color: 'var(--muted-foreground)' }}
        >
          <X className="tw-size-5" strokeWidth={2.25} />
          <span className="tw-sr-only">Close</span>
        </button>
      </div>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('tw-flex tw-flex-col tw-gap-2 tw-text-center sm:tw-text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('tw-flex tw-flex-col-reverse tw-gap-2 sm:tw-flex-row sm:tw-justify-end', className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<'h2'>) {
  return (
    <h2
      id={TITLE_ID}
      data-slot="dialog-title"
      className={cn('tw-text-lg tw-leading-none tw-font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      id={DESCRIPTION_ID}
      data-slot="dialog-description"
      className={cn('tw-text-muted-foreground tw-text-sm', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
